import { useSectionAccess } from "../../../auth/useSectionAccess";
import { FormEvent, useEffect, useState } from "react";
import { TableSkeleton } from "../../../components/Loading";
import { useTranslation } from "react-i18next";
import { catalogApi, type CategoryNode } from "../../../api/catalog";
import type { ApiError } from "../../../api/client";
import { useConfirm } from "../../../components/ConfirmDialog";
import { Select } from "../../../components/Select";
import { useToast } from "../../../components/Toast";
import { emptyRow, schemaError, toRows, toSchema, type SchemaRow } from "./attributeSchema";
import { CatalogAdminTabs } from "../../../components/catalog/CatalogAdminTabs";

const LOCALES = ["sr", "en", "de", "hr", "sl", "it", "es", "fr", "ru", "tr"] as const;

export default function AdminCategoriesPage() {
  const { canWrite } = useSectionAccess("catalog");
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const [tree, setTree] = useState<CategoryNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id?: string; parentId?: string } | null>(null);

  async function load() {
    setError(null);
    try {
      setTree(await catalogApi.tree("en"));
    } catch (err) {
      setError((err as ApiError).message);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function moveUp(node: CategoryNode) {
    await catalogApi.reorderCategory(node.id, Math.max(0, node.displayOrder - 1));
    await load();
  }

  async function moveDown(node: CategoryNode) {
    await catalogApi.reorderCategory(node.id, node.displayOrder + 1);
    await load();
  }

  async function handleDelete(node: CategoryNode) {
    if (!(await confirm({ message: t("admin.categories.confirmDelete", { name: node.name }), danger: true }))) return;
    try {
      await catalogApi.deleteCategory(node.id);
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.categories.title")}</h1>
        <button type="button" disabled={!canWrite} onClick={() => setEditing({})} style={primary}>{t("admin.categories.action.newRoot")}</button>
      </header>

      <CatalogAdminTabs active="categories" />

      {error && <div style={errorBox}>{error}</div>}

      {!tree ? (
        <TableSkeleton rows={6} cols={4} label={t("common.loading")} />
      ) : tree.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.categories.empty")}</p>
      ) : (
        <div style={{ background: "var(--surface)", borderRadius: 8, boxShadow: "var(--shadow-1)", overflow: "hidden" }}>
          <Tree
            nodes={tree}
            depth={0}
            onAddChild={(parentId) => setEditing({ parentId })}
            onEdit={(id) => setEditing({ id })}
            onDelete={handleDelete}
            onMoveUp={moveUp}
            onMoveDown={moveDown}
          />
        </div>
      )}

      {editing && canWrite && (
        <CategoryFormModal
          initialId={editing.id}
          parentId={editing.parentId}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function Tree({
  nodes, depth, onAddChild, onEdit, onDelete, onMoveUp, onMoveDown,
}: {
  nodes: CategoryNode[];
  depth: number;
  onAddChild: (parentId: string) => void;
  onEdit: (id: string) => void;
  onDelete: (n: CategoryNode) => void;
  onMoveUp: (n: CategoryNode) => void;
  onMoveDown: (n: CategoryNode) => void;
}) {
  const { t } = useTranslation();
  const { canWrite, canFull } = useSectionAccess("catalog");
  return (
    <div>
      {nodes.map((n) => (
        <div key={n.id}>
          <div style={{
            padding: "10px 12px",
            paddingLeft: 12 + depth * 20,
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <strong style={{ fontSize: 14 }}>{n.name}</strong>
              <span style={{ color: "var(--ink-4)", fontSize: 12 }}>{t("admin.categories.slugOrder", { slug: n.slug, order: n.displayOrder })}</span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button type="button" disabled={!canWrite} onClick={() => onMoveUp(n)} style={iconBtn} title={t("admin.categories.action.moveUp")}>↑</button>
              <button type="button" disabled={!canWrite} onClick={() => onMoveDown(n)} style={iconBtn} title={t("admin.categories.action.moveDown")}>↓</button>
              <button type="button" disabled={!canWrite} onClick={() => onAddChild(n.id)} style={linkBtn}>{t("admin.categories.action.addChild")}</button>
              <button type="button" disabled={!canWrite} onClick={() => onEdit(n.id)} style={linkBtn}>{t("action.edit")}</button>
              <button type="button" disabled={!canFull} onClick={() => onDelete(n)} style={dangerLink}>{t("action.delete")}</button>
            </div>
          </div>
          {n.children.length > 0 && (
            <Tree
              nodes={n.children}
              depth={depth + 1}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function CategoryFormModal({
  initialId, parentId, onClose, onSaved,
}: {
  initialId?: string;
  parentId?: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [slug, setSlug] = useState("");
  const [translations, setTranslations] = useState<Record<string, string>>({ en: "" });
  const [order, setOrder] = useState(0);
  const uiLocale = i18n.resolvedLanguage ?? "en";
  const [rows, setRows] = useState<SchemaRow[]>(toRows(undefined));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!initialId);

  useEffect(() => {
    if (!initialId) return;
    void catalogApi
      .getCategory(initialId)
      .then((c) => {
        setSlug(c.slug);
        setTranslations(c.translations);
        setOrder(c.displayOrder);
        setRows(toRows(c.attributeSchema));
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [initialId]);

  function updateRow(i: number, patch: Partial<SchemaRow>) {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!translations.en?.trim()) {
      setError(t("admin.categories.error.englishRequired"));
      return;
    }
    const dup = schemaError(rows);
    if (dup) {
      setError(t("admin.categories.schema.keyRequired", "Duplicate or empty attribute key: ") + dup);
      return;
    }
    setSubmitting(true);
    try {
      const cleaned = Object.fromEntries(Object.entries(translations).filter(([, v]) => v.trim()));
      const attributeSchema = toSchema(rows);
      if (initialId) {
        await catalogApi.updateCategory(initialId, { slug, translations: cleaned, displayOrder: order, attributeSchema });
      } else {
        await catalogApi.createCategory({ slug, parentId, translations: cleaned, displayOrder: order, attributeSchema });
      }
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>
          {initialId ? t("admin.categories.modal.edit") : parentId ? t("admin.categories.modal.newChild") : t("admin.categories.modal.newRoot")}
        </h2>
        {loading ? (
          <p>{t("common.loading")}</p>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label={t("admin.categories.field.slug")} required>
              <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} pattern="^[a-z0-9-]{2,80}$" required style={input} />
            </Field>
            <Field label={t("admin.categories.field.displayOrder")}>
              <input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} style={input} />
            </Field>
            <Field label={t("admin.categories.field.translations")}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {LOCALES.map((loc) => (
                  <div key={loc} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ width: 30, fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase" }}>{loc}</span>
                    <input
                      value={translations[loc] ?? ""}
                      onChange={(e) => setTranslations({ ...translations, [loc]: e.target.value })}
                      style={{ ...input, flex: 1 }}
                      placeholder={loc === "en" ? t("common.required") : t("common.optional")}
                    />
                  </div>
                ))}
              </div>
            </Field>
            <Field label={t("admin.categories.schema.title", "Attributes (schema)")}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {rows.length === 0 ? (
                  <span style={{ color: "var(--ink-4)", fontSize: 12 }}>
                    {t("admin.categories.schema.empty", "No attributes defined for this category yet.")}
                  </span>
                ) : (
                  rows.map((row, i) => (
                    <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                      <input
                        value={row.key}
                        onChange={(e) => updateRow(i, { key: e.target.value })}
                        placeholder={t("admin.categories.schema.keyPlaceholder", "key")}
                        style={{ ...input, flex: "1 1 110px", minWidth: 90 }}
                      />
                      {/* The label is written for the language the admin is
                          working in; other languages keep whatever they had. */}
                      <input
                        value={row.labels[uiLocale] ?? ""}
                        onChange={(e) => updateRow(i, { labels: { ...row.labels, [uiLocale]: e.target.value } })}
                        placeholder={t("admin.categories.schema.labelPlaceholder", "label ({{lang}})", { lang: uiLocale.toUpperCase() })}
                        title={t("admin.categories.schema.labelHint", "Shown to users in {{lang}}; other languages are edited by switching the portal language.", { lang: uiLocale.toUpperCase() })}
                        style={{ ...input, flex: "1 1 110px", minWidth: 90 }}
                      />
                      <Select<SchemaRow["type"]>
                        value={row.type}
                        onChange={(type) => updateRow(i, { type })}
                        options={[
                          { value: "string", label: "string" },
                          { value: "number", label: "number" },
                          { value: "select", label: "select" },
                          { value: "multiselect", label: "multiselect" },
                          { value: "boolean", label: "boolean" },
                        ]}
                        style={{ flex: "0 0 auto", minWidth: 110 }}
                      />
                      <input
                        value={row.unit}
                        onChange={(e) => updateRow(i, { unit: e.target.value })}
                        placeholder={t("admin.categories.schema.unitPlaceholder", "unit")}
                        style={{ ...input, flex: "0 1 70px", minWidth: 50 }}
                      />
                      <input
                        value={row.options}
                        onChange={(e) => updateRow(i, { options: e.target.value })}
                        disabled={row.type !== "select" && row.type !== "multiselect"}
                        placeholder={t("admin.categories.schema.optionsPlaceholder", "options (CSV)")}
                        style={{
                          ...input,
                          flex: "1 1 130px",
                          minWidth: 100,
                          background: row.type === "select" || row.type === "multiselect" ? "var(--surface)" : "var(--surface-2)",
                        }}
                      />
                      {(row.type === "select" || row.type === "multiselect")
                        && row.options.split(",").map((o) => o.trim()).filter(Boolean).length > 0 && (
                        // One label per option, for the language being worked in
                        // — without it an option an admin adds reads raw in all
                        // ten languages.
                        <div style={{ flexBasis: "100%", display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 4 }}>
                          {row.options.split(",").map((o) => o.trim()).filter(Boolean).map((o) => (
                            <label key={o} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, color: "var(--ink-3)" }}>
                              <span style={{ fontFamily: "var(--mono)" }}>{o}</span>
                              <input
                                value={row.optionLabels[o]?.[uiLocale] ?? ""}
                                onChange={(e) => updateRow(i, {
                                  optionLabels: {
                                    ...row.optionLabels,
                                    [o]: { ...(row.optionLabels[o] ?? {}), [uiLocale]: e.target.value },
                                  },
                                })}
                                placeholder={uiLocale.toUpperCase()}
                                style={{ ...input, width: 120, padding: "4px 6px", fontSize: 12 }}
                              />
                            </label>
                          ))}
                        </div>
                      )}
                      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
                        <input
                          type="checkbox"
                          checked={row.filterable}
                          onChange={(e) => updateRow(i, { filterable: e.target.checked })}
                        />
                        {t("admin.categories.schema.filterable", "filter")}
                      </label>
                      <button
                        type="button"
                        onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
                        style={dangerLink}
                      >
                        {t("admin.categories.schema.remove", "Remove")}
                      </button>
                    </div>
                  ))
                )}
                <div>
                  <button type="button" onClick={() => setRows([...rows, emptyRow()])} style={linkBtn}>
                    {t("admin.categories.schema.add", "+ Add attribute")}
                  </button>
                </div>
              </div>
            </Field>
            {error && <div style={errorBox}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" onClick={onClose} style={secondary}>{t("action.cancel")}</button>
              <button type="submit" disabled={submitting} style={primary}>
                {submitting ? t("admin.categories.action.saving") : t("action.save")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={{ color: "var(--ink-3)" }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14 };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-ink)", cursor: "pointer", fontSize: 12, padding: "4px 6px" };
const dangerLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", cursor: "pointer", fontSize: 12, padding: "4px 6px" };
const iconBtn: React.CSSProperties = { padding: "2px 8px", background: "var(--surface-2)", border: "1px solid var(--border-strong)", borderRadius: 4, fontSize: 12, cursor: "pointer" };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 540, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14 };
